import {getPlayerBonusConfig, PLAYER_BONUS_LABELS, PLAYER_BONUS_ORDER} from '../../configs/playerBonus';
import {getLocalBotLeagueId} from '../../configs/localBotLeagues';
import {getBotSupplySizeBonusForLeague} from '../../configs/botLeagueSupplyGrowth';
import {getBotDiceBonusLines} from './getBotDiceBonusLines';

/**
 * Уровни бонуса, с которыми бот локальной партии получает прибавку к росту
 * отрядов: его лига-эквивалент (см. configs/localBotLeagues) ускоряет рост
 * (см. configs/botLeagueSupplyGrowth). В порядке переключателя бонуса.
 */
export const LOCAL_BOT_GROWTH_BONUS_TYPES = PLAYER_BONUS_ORDER.filter(
  (bonusType) => Boolean(getBotSupplySizeBonusForLeague(getLocalBotLeagueId(bonusType)))
);

/**
 * Уровни бонуса, которые боту локальной партии подкручивают кости (пол d6, см.
 * getBotD6MinRoll) — сейчас только «божество». Живому игроку тот же уровень
 * костей не дает.
 */
export const LOCAL_BOT_DICE_BONUS_TYPES = PLAYER_BONUS_ORDER.filter(
  (bonusType) => getBotDiceBonusLines(getPlayerBonusConfig(bonusType).d6MinRoll).length > 0
);

/**
 * Уровни бонуса с очками победы к доходу за каждый город (см.
 * calcPlayerIncomeBreakdown) — сейчас только «божество». Очки, как и
 * экономику, уровень дает любому игроку, не только боту.
 */
export const LOCAL_BONUS_WIN_POINTS_TYPES = PLAYER_BONUS_ORDER.filter(
  (bonusType) => getPlayerBonusConfig(bonusType).winPointsIncomePerCityBonus > 0
);

const joinWithAnd = (items) => (
  items.length > 1 ? `${items.slice(0, -1).join(', ')} и ${items[items.length - 1]}` : items.join('')
);

const getLabel = (bonusType) => `«${PLAYER_BONUS_LABELS[bonusType]}»`;

const getLevelsWords = (bonusTypes) => (bonusTypes.length > 1 ? ['уровни', 'дают'] : ['уровень', 'дает']);

const lowerFirst = (text) => text.charAt(0).toLowerCase() + text.slice(1);

/**
 * Пояснение к выбору бонуса БОТУ в настройке локальной игры: уровни бонуса,
 * которые дают боту еще и прибавку к росту отрядов. Уровни берутся из
 * конфигов, поэтому подсказка не разойдется с игрой. Пустая строка — таких
 * уровней нет.
 */
const getLocalBotGrowthBonusNote = () => {
  if (LOCAL_BOT_GROWTH_BONUS_TYPES.length === 0) {
    return '';
  }
  const [levelsWord, verb] = getLevelsWords(LOCAL_BOT_GROWTH_BONUS_TYPES);
  return `Боту ${levelsWord} ${joinWithAnd(LOCAL_BOT_GROWTH_BONUS_TYPES.map(getLabel))} ${verb} еще и бонус к `
    + 'росту размера отрядов — как у бота той же лиги в рейтинговом матче (см. раздел «Боты» в правилах).';
};

export const LOCAL_BOT_GROWTH_BONUS_NOTE = getLocalBotGrowthBonusNote();

/**
 * То же про кости: какие уровни и что именно дают боту (строки — общие с
 * подсказкой значка бота, см. getBotDiceBonusLines). Пустая строка — кости
 * честные при любом уровне.
 */
const getLocalBotDiceBonusNote = () => {
  if (LOCAL_BOT_DICE_BONUS_TYPES.length === 0) {
    return '';
  }
  const parts = LOCAL_BOT_DICE_BONUS_TYPES.map((bonusType) => (
    `${getLabel(bonusType)} — ${lowerFirst(getBotDiceBonusLines(getPlayerBonusConfig(bonusType).d6MinRoll)[0])}`
  ));
  return `Кости боту подкручивает только ${LOCAL_BOT_DICE_BONUS_TYPES.length > 1 ? 'уровни' : 'уровень'} `
    + `${parts.join('; ')}.`;
};

export const LOCAL_BOT_DICE_BONUS_NOTE = getLocalBotDiceBonusNote();

/**
 * Пояснение для всех: уровни, которые сверх экономики дают очки победы к
 * доходу. Пустая строка — таких уровней нет.
 */
const getLocalBonusWinPointsNote = () => {
  if (LOCAL_BONUS_WIN_POINTS_TYPES.length === 0) {
    return '';
  }
  const [levelsWord, verb] = getLevelsWords(LOCAL_BONUS_WIN_POINTS_TYPES);
  return `${levelsWord.charAt(0).toUpperCase()}${levelsWord.slice(1)} `
    + `${joinWithAnd(LOCAL_BONUS_WIN_POINTS_TYPES.map(getLabel))} ${verb} еще и очки победы к доходу за каждый город.`;
};

export const LOCAL_BONUS_WIN_POINTS_NOTE = getLocalBonusWinPointsNote();

/**
 * Подсказка кнопки бонуса в настройке локальной игры. Уровень бонуса любому
 * игроку дает экономику, а «божество» — еще и очки победы к доходу: строка об
 * этом — всем. Игроку, который станет ботом локальной партии (значок, прибавка
 * к росту отрядов и подкрутка костей, см. prepareLocalMatchPlayers и
 * getBotD6MinRoll), добавляются строки про рост и кости; живому игроку и в
 * редакторе миссий их нет: ни роста, ни костей уровень им не дает.
 *
 * @param {string} baseTooltip - общее пояснение бонуса локальной игры
 * @param {boolean} isLocalBot - будет ли игрок ботом локальной партии
 * @returns {string}
 */
export const getLocalBonusTooltip = (baseTooltip, isLocalBot) => [
  baseTooltip,
  LOCAL_BONUS_WIN_POINTS_NOTE,
  ...(isLocalBot ? [LOCAL_BOT_GROWTH_BONUS_NOTE, LOCAL_BOT_DICE_BONUS_NOTE] : []),
].filter(Boolean).join('\n');
